/**
 * Spinner - Loading Indicator Component
 *
 * Animated spinning loader for async operations.
 * Renders as Box with SVG animation.
 *
 * @example
 * <Spinner size="md" />
 * <Spinner size="lg" className="text-red-600" />
 */
import { forwardRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { Box } from "@/shared/ui/base";
import { cn } from "@/shared/lib/utils/cn";
import type { BaseComponentProps } from "@/shared/ui/base";

const spinnerVariants = cva("animate-spin", {
  variants: {
    size: {
      sm: "w-4 h-4",
      md: "w-6 h-6",
      lg: "w-8 h-8",
      xl: "w-12 h-12",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

export interface SpinnerProps
  extends BaseComponentProps, VariantProps<typeof spinnerVariants> {}

/**
 * Spinner component
 * Renders as a Box with an animated Loader2 icon
 */
export const Spinner = forwardRef<HTMLDivElement, SpinnerProps>(
  ({ size = "md", className, ...props }, ref) => {
    return (
      <Box
        ref={ref}
        className={cn("inline-flex items-center justify-center", className)}
        {...props}
      >
        <Loader2 className={spinnerVariants({ size })} />
      </Box>
    );
  },
);

Spinner.displayName = "Spinner";
